<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.fetch_quests', 2),
                        href: null,
                    },
                    {
                        name: fetchQuest.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="bg-gray-50 sm:rounded-lg bg-no-repeat bg-center bg-cover" :style="style">
            <div class="flex flex-col justify-end">
                <div class="p-4" v-if="fetchQuest.is_reward_shown">
                    <div class="">
                        {{ $t('messages.prize_to_be_shared') }}
                        ({{
                            fetchQuest.is_reward_evenly_split
                                ? $t('messages.reward_evenly_split')
                                : $t('messages.reward_not_evenly_split')
                        }}) :
                    </div>
                    <div class="flex space-x-2 mt-3">
                        <div>
                            <img class="h-6 w-6" src="/assets/gamification/currency/coin.svg" alt="emerald" />
                        </div>
                        <div class="">
                            {{ fetchQuest.coins_amount }}
                        </div>
                    </div>
                </div>

                <div class="mt-5 px-4">
                    <div class="grid grid-cols-2 lg:grid-cols-6 gap-6">
                        <div v-for="item in items" class="bg-gray-100 p-2 rounded-lg" :key="item.id">
                            <img :src="item.image_path" alt="" />
                            <div>{{ item.name }} ({{ item.quantity }})</div>
                            <div class="text-gray-600 text-sm">
                                {{ item.options_description }}
                            </div>
                            <div class="flex gap-x-2">
                                <div v-if="item.description">
                                    <Tippy>
                                        <button>
                                            <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                        </button>
                                        <template #content>
                                            {{ item.description }}
                                        </template>
                                    </Tippy>
                                </div>
                                <div v-if="item.uses_stock">
                                    <Tippy>
                                        <button>
                                            <IconBox class="w-6 h-6 text-gray-400" v-if="item.stock_amount" />
                                            <IconBoxOff class="w-6 h-6 text-gray-400" v-else />
                                        </button>
                                        <template #content>
                                            {{ item.stock_message }}
                                        </template>
                                    </Tippy>
                                </div>
                                <div v-if="item.max_total_uses">
                                    <Tippy>
                                        <button>
                                            <IconInfinityOff class="w-6 h-6 text-gray-400" />
                                        </button>
                                        <template #content>
                                            {{ item.max_total_uses_message }}
                                        </template>
                                    </Tippy>
                                </div>
                            </div>
                            <div>
                                <ProgressBar
                                    :percent="Math.min(100, Math.round((item.items_given_count / item.quantity) * 100))"
                                    class="w-full"
                                    color="indigo"
                                >
                                    {{ item.items_given_count }} / {{ item.quantity }}
                                </ProgressBar>
                            </div>
                            <div class="flex justify-between items-center mt-1">
                                <div>
                                    <BadgePill :color="item.count ? 'indigo' : 'gray'">
                                        &times;{{ item.count }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <InertiaLinkButton
                                        as="button"
                                        :href="item.action.url"
                                        type="white"
                                        method="patch"
                                        v-if="item.can.use"
                                        :data="{
                                            gamification_item_id: item.action.gamification_item_id,
                                        }"
                                        preserve-scroll
                                    >
                                        {{ item.action.text }}
                                    </InertiaLinkButton>
                                </div>
                            </div>
                            <div class="flex justify-between items-center mt-1" v-if="item.can.purchase">
                                <div>
                                    <BadgePill color="yellow">
                                        {{ item.price }}
                                    </BadgePill>
                                </div>
                                <InertiaLinkButton as="button" :href="item.purchase_url" type="white" method="post">
                                    {{ $t('messages.buy') }}
                                </InertiaLinkButton>
                            </div>
                            <div class="flex justify-between items-center mt-1" v-if="item.required_level_description">
                                {{ $t('messages.level_required') }}:
                                {{ item.required_level_description }}
                            </div>
                            <div class="flex justify-between items-center mt-1" v-if="item.extra_actions.count">
                                <button
                                    @click="item.panel_open = true"
                                    type="button"
                                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    <svg
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="w-4 h-4"
                                    >
                                        <path
                                            d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                                        ></path>
                                    </svg>
                                </button>
                                <SlideOver :show="item.panel_open" @close="item.panel_open = false" :title="item.name">
                                    <img :src="item.image_path" alt="" />
                                    <SellSlideOverWidget
                                        v-if="item.extra_actions.actions.sell_back"
                                        :action="item.extra_actions.actions.sell_back"
                                        class="mt-3"
                                    />
                                </SlideOver>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="py-5 sm:p-6">
                    <AppCard>
                        <p v-html="fetchQuest.description" />
                    </AppCard>
                </div>
            </div>
        </div>

        <div>
            <AlertMessage class="mt-5 inline-block" type="info">
                <template v-slot:title>
                    <span>{{ fetchQuest.event_ending_date_message }}</span>

                    <div v-if="fetchQuest.max_actions_per_student" class="mt-3">
                        {{
                            $t('messages.this_event_allows_X_actions', {
                                count: fetchQuest.max_actions_per_student,
                            })
                        }}
                    </div>
                </template>
                <template v-slot:body>
                    {{ $t('messages.information_visible_by_all_teachers') }}
                </template>
            </AlertMessage>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.actions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.when') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(action, index) in fetchQuest.actions"
                                :key="action.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <img
                                        :src="action.avatar_or_default"
                                        :alt="action.full_name"
                                        :title="action.full_name"
                                        class="rounded-full h-12"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <img :src="action.action_image" class="h-12" />
                                    <div>
                                        {{ action.name }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ action.diffForHumans }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <VictoryModal :message="$t('messages.fetch_quest_finished_message')" v-if="meta.info.is_just_finished" />
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import InertiaLinkButton from '@/components/InertiaLinkButton.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import SlideOver from '@/components/SlideOver.vue';
import SellSlideOverWidget from '@/components/SellSlideOverWidget.vue';
import { Tippy } from 'vue-tippy';
import { IconBox, IconBoxOff, IconInfinityOff, IconInfoCircle } from '@tabler/icons-vue';
import AppCard from '@/Shared/AppCard.vue';
import VictoryModal from '@/components/pageSpecific/VictoryModal.vue';
import AlertMessage from '@/components/AlertMessage.vue';

export default {
    components: {
        AlertMessage,
        VictoryModal,
        AppCard,
        PanelPage,
        BadgePill,
        InertiaLinkButton,
        ProgressBar,
        SlideOver,
        SellSlideOverWidget,
        Tippy,
        IconBox,
        IconBoxOff,
        IconInfinityOff,
        IconInfoCircle,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'fetchQuest', 'items'],
    computed: {
        style: function () {
            let styleObject = {};
            if (this.fetchQuest.image_path) {
                styleObject.backgroundImage = `url(${this.fetchQuest.image_path})`;
            }
            return styleObject;
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
